import { DashboardResponse } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { enc, SHA256 } from 'crypto-js';
import { DataLayerWindow } from '../../index';
import { AmexCardType, USER_LOGGED_IN_STATUS } from '../models';
import {
  AnalyticsData,
  PreviousPageDetails,
  User
} from '../models/analytics/analytics.interfaces';
import {
  getCountry,
  getEmailDomain,
  getLanguage,
  getSystemEnvironment,
  getValidValue
} from './analytics-helpers.utils';

export const getPreviousPageDetails = (
  previousUrl?: string | null
): PreviousPageDetails => {
  const url = previousUrl || '';
  if (!url) return { url: '', name: '' };
  return {
    url,
    name: getPreviousPageName(url)
  };
};

export const getPreviousPageName = (pathname: string): string => {
  if (pathname === '/') return 'dal:home:no-subsection';
  if (pathname) return `dal${pathname.split('/').join(':')}`;
  return '';
};

export const getPageLoadTime = (pageLoadStartTime: number): number => {
  return pageLoadStartTime
    ? Number(((Date.now() - pageLoadStartTime) / 1000).toFixed(3))
    : 0;
};

export const getPrefUIContent = (
  prefUIData: string | null
): { country: string; language: string } => {
  const prefUI = prefUIData || '';
  return {
    country: prefUI.split('-')[1],
    language: prefUI.split('-')[0]
  };
};

export const getAccountData = (response: DashboardResponse) => {
  const cobrandList = response.cobrandCardtypeDescList || '';
  const cobrandType = cobrandList
    ? cobrandList
        .split(':')
        .map((item) => item.trim().toLowerCase())
        .join(',')
    : '';

  return {
    available_miles: response.smBalance?.toString() || '0',
    cobrand_type: cobrandType
  };
};

export const getPageInfoDataForAnalytics = (
  analyticsData: AnalyticsData,
  prefUI: string,
  pageLoadStart: number,
  previousUrl?: string | null
): AnalyticsData => {
  const prevPage = getPreviousPageDetails(previousUrl);
  return Object.assign({}, analyticsData, {
    page: Object.assign({}, analyticsData.page, {
      info: Object.assign({}, analyticsData.page?.info, {
        page_url: window.location.pathname,
        previous_page_name: prevPage.name,
        previous_page_url: prevPage.url,
        country: getCountry(prefUI),
        language: getLanguage(prefUI),
        sys_env: getSystemEnvironment(),
        page_load_time: getPageLoadTime(pageLoadStart)
      })
    })
  });
};

export const getEncryptedEmail = (emailAddress: string): string => {
  return emailAddress ? SHA256(emailAddress).toString(enc.Base64) : '';
};

export const getCardLabelByValue = (value: string): string => {
  const found = AmexCardType.find((option) => option.id === value);
  return found ? found.value : '';
};

export const setAdobeAnalytics = (event: AnalyticsData) => {
  const dataLayerWindow = window as unknown as DataLayerWindow;
  dataLayerWindow.adobeDataLayer = dataLayerWindow.adobeDataLayer || [];
  dataLayerWindow.adobeDataLayer.push(event);
};

export const getUserDataForAnalytics = (
  analyticsData: AnalyticsData,
  response: DashboardResponse
): User => {
  const emailAddress = getValidValue(response?.primaryEmailAddress);

  return {
    ...analyticsData.user,
    profile: {
      skymiles_number: getValidValue(response.smNumber),
      medalion_tier: getValidValue(response.membershipLevel),
      email: '',
      email_domain: getEmailDomain(emailAddress),
      encrypted_email: getEncryptedEmail(emailAddress),
      available_miles: response.smBalance ? Number(response.smBalance) : 0,
      company_name: '',
      cobrand_type: getValidValue(response.smeCardTypes),
      skymiles_tier: getValidValue(response.membershipLevel),
      amex_card_type: getCardLabelByValue(response.smeCardTypes)
    },
    attributes: {
      ...analyticsData.user.attributes,
      is_logged_in: USER_LOGGED_IN_STATUS.AUTHENTICATED,
      login_state: USER_LOGGED_IN_STATUS.HOT,
      login_method: ''
    }
  };
};
